
import React, { useState, useEffect } from 'react';
import { MemberModel, BranchModel, SiteSettingModel, UserRole, ApiSettingModel, StudentGuardian, GRADE_TYPES, CompanyName } from '../../../../models';
import { UserPlus, Search, Filter, Users, Link as LinkIcon, Lock, Download as DownloadIcon, Plus, Trash2, Building2, GraduationCap } from 'lucide-react';
import { collection, query, where, onSnapshot, doc, deleteDoc, updateDoc, getDoc } from 'firebase/firestore';
import { db, rtdb } from '../../../../firebase';
import { ref, onValue } from 'firebase/database';
import MemberCard from './studentMemberCard';
import AdminPageHeader from '../../frame/RightPanelHeader';
import Pagination from '../../../../components/ui/Pagination';
import NeisSearchModal, { NeisSchoolData } from '../../../../components/NeisSearchModal';

interface MemberRootProps {
    members: MemberModel[];
    branches: BranchModel[];
    siteSettings: SiteSettingModel;
    onSelectMember: (member: MemberModel) => void;
    selectedMemberId?: string;
    onAddMember: (member: MemberModel) => void;
    onUpdateMember?: (member: MemberModel) => void;
    onDeleteMember?: (memberId: string) => void;
}

type SortField = 'name' | 'role' | 'grade' | 'school' | 'branch';
type SortOrder = 'asc' | 'desc';

const ROLE_LABELS: Record<UserRole, string> = {
    super_admin: '최고 관리자',
    manager: '매니저',
    staff: '직원',
    teacher: '선생님',
    student: '학생',
    parent: '학부모',
};

const MemberRoot: React.FC<MemberRootProps> = ({
    members,
    branches,
    siteSettings,
    onSelectMember,
    selectedMemberId,
    onAddMember,
    onUpdateMember,
    onDeleteMember
}) => {
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingMember, setEditingMember] = useState<MemberModel | undefined>(undefined);
    const [isSchoolModalOpen, setIsSchoolModalOpen] = useState(false);

    // Search State
    const [searchTerm, setSearchTerm] = useState('');

    // Member Filters
    const [roleFilter, setRoleFilter] = useState<UserRole | 'all'>('student');
    const [sortField, setSortField] = useState<SortField>('name');
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // New Filter States
    const [filterStatus, setFilterStatus] = useState<'all' | '재원' | '휴원' | '퇴원'>('all');
    const [filterGrade, setFilterGrade] = useState<string>('all');
    const [schoolSearchTerm, setSchoolSearchTerm] = useState('');
    const [isPresenceDropdownOpen, setIsPresenceDropdownOpen] = useState(false);

    // Presence State
    const [presence, setPresence] = useState<Record<string, { state: string; last_changed: number }>>({});
    const [presenceError, setPresenceError] = useState<string | null>(null);

    useEffect(() => {
        const statusRef = ref(rtdb, 'status');
        const unsubscribe = onValue(statusRef, (snapshot) => {
            const data = snapshot.val();
            if (snapshot.exists()) {
                setPresence(data);
                setPresenceError(null);
            } else {
                setPresence({});
            }
        }, (error) => {
            setPresenceError(error.message);
        });
        return () => unsubscribe();
    }, []);

    // List of online members (Students only)
    const onlineMembers = members.filter(m => m.role === 'student' && presence[m.id]?.state === 'online');


    const getBranchName = (branchId?: string) => {
        if (!branchId) return '-';
        const branchesList = branches || []; // Ensure branches is array
        const branch = branchesList.find(b => b.id === branchId);
        return branch?.name || '-';
    };

    const handleShareLink = async (member: MemberModel) => {
        // 1. Check if key exists, if not generate and save
        let accessKey = member.parentAccessKey;
        if (!accessKey) {
            accessKey = crypto.randomUUID();
            // Call onUpdateMember to save the new key
            if (onUpdateMember) {
                await onUpdateMember({ ...member, parentAccessKey: accessKey });
            }
        }

        // 2. Construct URL
        const url = `${window.location.origin}/student-view/${member.id}?key=${accessKey}`;

        // 3. Check for Parent Email
        const pEmail = member.guardians?.[0]?.email || member.parentEmail;
        if (pEmail) {
            const choice = confirm(`학부모 이메일(${pEmail})이 등록되어 있습니다.\n\n[확인]: 이메일 앱 열기\n[취소]: 링크만 복사`);
            if (choice) {
                const subject = `[${CompanyName}] ${member.name} 학생 학습 현황 포털 링크 안내`;
                const body = `학부모님, 안녕하세요.\n\n자녀(${member.name})분의 학습 현황을 확인하실 수 있는 전용 포털 링크를 안내해 드립니다.\n아래 링크를 클릭하시면 별도의 로그인 없이 바로 확인하실 수 있습니다.\n\n[학습 포털 링크]:\n${url}\n\n감사합니다.`;
                window.location.href = `mailto:${pEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                return;
            }
        }

        // 4. Default: Copy to clipboard prompt
        prompt("학부모용 열람 링크입니다. 복사해서 전달해주세요:", url);
    };

    const handleUpdateClick = (member: MemberModel) => {
        // Ensure guardians array exists, if not, try to migrate from legacy parent fields
        let initialGuardians = member.guardians || [];
        if (initialGuardians.length === 0 && (member.parentPhone || member.parentEmail)) {
            initialGuardians = [{
                relation: (member.parentRole as any) || '부',
                name: '',
                phone: member.parentPhone || '',
                email: member.parentEmail || ''
            }];
        }
        if (initialGuardians.length === 0) {
            initialGuardians = [{ relation: '부', name: '', phone: '', email: '' }];
        }

        setEditingMember({ ...member, guardians: initialGuardians });
        setIsFormOpen(true);
    };

    const handleSaveMember = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingMember || !onUpdateMember) return;

        // Legacy mapping sync (optional, but good for backward compatibility)
        const mainGuardian = editingMember.guardians?.[0];
        const updatedMember = {
            ...editingMember,
            parentRole: mainGuardian?.relation as any,
            parentPhone: mainGuardian?.phone,
            parentEmail: mainGuardian?.email,
        };

        await onUpdateMember(updatedMember);
        setIsFormOpen(false);
        setEditingMember(undefined);
    };

    // Guardian Handlers
    const handleAddGuardian = () => {
        if (!editingMember) return;
        setEditingMember({
            ...editingMember,
            guardians: [...(editingMember.guardians || []), { relation: '부', name: '', phone: '', email: '' }]
        });
    };

    const handleRemoveGuardian = (index: number) => {
        if (!editingMember || !editingMember.guardians) return;
        setEditingMember({
            ...editingMember,
            guardians: editingMember.guardians.filter((_, i) => i !== index)
        });
    };

    const handleGuardianChange = (index: number, field: keyof StudentGuardian, value: string) => {
        if (!editingMember || !editingMember.guardians) return;
        const newGuardians = [...editingMember.guardians];
        newGuardians[index] = { ...newGuardians[index], [field]: value };
        setEditingMember({ ...editingMember, guardians: newGuardians });
    };


    // --- Filter Logic for Members ---
    let filteredMembers = members.filter(m => {
        const matchesName = m.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesRole = m.role === 'student';

        // NEW FILTERS
        const matchesStatus = filterStatus === 'all' ? true : (m.enrollmentStatus || '재원') === filterStatus; // Default to '재원' if undefined for backward compat? Or handle strict undefined. Let's assume undefined is okay or needs handling. Treat only explicit match.
        // Actually, many existing members might not have enrollmentStatus. Let's assume undefined means '재원' effectively for now, OR show them only in 'all'.
        // Let's stick to strict match if value is present. If filter is 'all', pass.
        // If filter is specific, match value.

        const matchesGrade = filterGrade === 'all' ? true : m.grade === filterGrade;
        const matchesSchool = schoolSearchTerm === '' ? true : (m.schoolName || '').toLowerCase().includes(schoolSearchTerm.toLowerCase());

        return matchesName && matchesRole && matchesStatus && matchesGrade && matchesSchool;
    });

    // Default Sort by Name ASC
    filteredMembers.sort((a, b) => a.name.localeCompare(b.name));

    const totalPages = Math.ceil(filteredMembers.length / itemsPerPage);
    const paginatedMembers = filteredMembers.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, itemsPerPage, filterStatus, filterGrade, schoolSearchTerm]);

    const handleExportAllData = async () => {
        // Filter only students
        const students = filteredMembers.filter(m => m.role === 'student');

        if (students.length === 0) {
            alert('내보낼 학생 데이터가 없습니다.');
            return;
        }

        const confirmExport = confirm(`총 ${students.length}명의 학생 전체 정보를 엑셀(CSV)로 내보내시겠습니까?`);
        if (!confirmExport) return;

        let csvContent = "이름,전화번호,학년,학교,상태,지점,부모님 관계,부모님 성함,부모님 연락처,부모님 이메일,접속 링크\n";

        for (const s of students) {
            const branchName = getBranchName(s.branchId);

            // Get Primary Guardian Info
            const guardian = s.guardians?.[0];
            const pRelation = guardian?.relation || s.parentRole || '-';
            const pName = guardian?.name || '-';
            const pPhone = guardian?.phone || s.parentPhone || '-';
            const pEmail = guardian?.email || s.parentEmail || '-';

            // Format Phone with hyphens (Excel treats 010-xxxx-xxxx as text)
            const formatPhone = (str?: string) => {
                if (!str) return '-';
                const clean = str.replace(/[^0-9]/g, '');
                if (clean.length === 11) {
                    return clean.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
                } else if (clean.length === 10) {
                    // 02-1234-5678 or 011-123-4567 case
                    return clean.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3').replace(/(\d{2})(\d{4})(\d{4})/, '$1-$2-$3');
                }
                return clean;
            };

            const fmtPhone = formatPhone(s.phone);
            const fmtPPhone = formatPhone(pPhone !== '-' ? pPhone : undefined);

            // Portal Link Generation
            let key = s.parentAccessKey;
            if (!key) {
                key = crypto.randomUUID();
                if (onUpdateMember) {
                    await onUpdateMember({ ...s, parentAccessKey: key });
                }
            }
            const link = `${window.location.origin}/student-view/${s.id}?key=${key}`;

            const row = [
                s.name,
                fmtPhone,
                s.grade,
                s.schoolName || '-',
                s.enrollmentStatus || '재원',
                branchName,
                pRelation,
                pName,
                fmtPPhone,
                pEmail,
                link
            ].map(val => `"${val}"`).join(",");

            csvContent += row + "\n";
        }

        // BOM for Excel Korean support
        const BOM = "\uFEFF";
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `학생_전체_명단_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="flex flex-col h-full bg-white overflow-hidden w-full">
            <AdminPageHeader
                title="학생 관리"
                icon={Users}
                subtitle="학생 정보를 등록하고 관리합니다"
                actions={
                    <div className="flex gap-2 items-center">
                        <div className="relative">
                            <button
                                onClick={() => setIsPresenceDropdownOpen(!isPresenceDropdownOpen)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black transition-all shadow-sm ${presenceError
                                    ? 'bg-red-50 text-red-600 ring-1 ring-red-200'
                                    : onlineMembers.length > 0
                                        ? 'bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200 hover:bg-emerald-100'
                                        : 'bg-slate-50 text-slate-400 ring-1 ring-slate-100'
                                    }`}
                            >
                                <span className={`w-2 h-2 rounded-full ${onlineMembers.length > 0 ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`}></span>
                                Live: {onlineMembers.length}
                            </button>

                            {isPresenceDropdownOpen && onlineMembers.length > 0 && (
                                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-100 py-2 z-[100] animate-in fade-in zoom-in-95 duration-150">
                                    <div className="px-3 py-1 border-b border-slate-50 mb-1">
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">접속 중인 학생</span>
                                    </div>
                                    <div className="max-h-60 overflow-y-auto">
                                        {onlineMembers.map(m => (
                                            <div key={m.id} className="px-4 py-2 hover:bg-slate-50 flex items-center gap-2 transition-colors cursor-pointer" onClick={() => { onSelectMember(m); setIsPresenceDropdownOpen(false); }}>
                                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                                                <span className="text-sm font-bold text-slate-700">{m.name}</span>
                                                <span className="text-[10px] text-slate-400 font-medium">({m.grade})</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {isPresenceDropdownOpen && onlineMembers.length === 0 && (
                                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-100 p-4 z-[100] text-center">
                                    <p className="text-xs font-bold text-slate-400">접속 중인 학생이 없습니다</p>
                                </div>
                            )}
                        </div>

                        <button
                            onClick={handleExportAllData}
                            className="bg-emerald-600 text-white px-4 py-2 rounded-xl text-sm font-bold hover:bg-emerald-700 transition-all flex items-center gap-2 shadow-sm"
                        >
                            <DownloadIcon size={18} />
                            전체 정보 엑셀 다운
                        </button>
                    </div>
                }
            />

            {/* Shared Search Bar Area */}
            <div className="px-8 py-5 border-b border-slate-200 bg-white shrink-0 z-10 w-full">
                <div className="flex items-center justify-between mb-4 w-full">
                    <div className="flex items-center gap-3">
                        <p className="text-sm font-bold text-slate-500">
                            전체 {filteredMembers.length}명
                        </p>
                        <select
                            value={itemsPerPage}
                            onChange={(e) => setItemsPerPage(Number(e.target.value))}
                            className="bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-xs font-bold text-slate-600 outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            <option value={10}>10명씩 보기</option>
                            <option value={30}>30명씩 보기</option>
                            <option value={50}>50명씩 보기</option>
                            <option value={100}>100명씩 보기</option>
                        </select>
                    </div>

                    <div className="flex items-center gap-2">
                        {/* Status Filter */}
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value as any)}
                            className="h-9 bg-white border border-slate-200 rounded-lg px-3 text-xs font-bold text-slate-600 outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            <option value="all">전체 상태</option>
                            <option value="재원">재원</option>
                            <option value="휴원">휴원</option>
                            <option value="퇴원">퇴원</option>
                        </select>

                        {/* Grade Filter */}
                        <select
                            value={filterGrade}
                            onChange={(e) => setFilterGrade(e.target.value)}
                            className="h-9 bg-white border border-slate-200 rounded-lg px-3 text-xs font-bold text-slate-600 outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            <option value="all">전체 학년</option>
                            {(siteSettings.grades && siteSettings.grades.length > 0 ? siteSettings.grades : GRADE_TYPES).map(g => (
                                <option key={g} value={g}>{g}</option>
                            ))}
                        </select>

                        {/* School Filter Input */}
                        <div className="relative w-40">
                            <input
                                type="text"
                                placeholder="학교명 필터"
                                value={schoolSearchTerm}
                                onChange={(e) => setSchoolSearchTerm(e.target.value)}
                                className="w-full h-9 bg-white border border-slate-200 rounded-lg pl-3 pr-8 text-xs font-bold text-slate-600 outline-none focus:ring-2 focus:ring-indigo-500 placeholder-slate-400"
                            />
                            {schoolSearchTerm ? (
                                <button
                                    onClick={() => setSchoolSearchTerm('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500"
                                >
                                    <Trash2 size={12} />
                                </button>
                            ) : (
                                <Building2 className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none" size={14} />
                            )}
                        </div>
                    </div>
                </div>

                {/* Bottom Row: Search Input & Filter Select */}
                <div className="flex gap-3 w-full">
                    {/* Search Input Filter */}
                    <div className="relative flex-1 group">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={18} />
                        <input
                            type="text"
                            placeholder="이름 검색..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-12 pr-4 py-3 text-sm font-medium outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all shadow-sm"
                        />
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar p-8 bg-slate-50 w-full">
                <div className="space-y-3 w-full">
                    {paginatedMembers.map((member) => (
                        <MemberCard
                            key={member.id}
                            member={member}
                            branches={branches}
                            isSelected={selectedMemberId === member.id}
                            isOnline={presence[member.id]?.state === 'online'}
                            onSelect={onSelectMember}
                            onUpdate={handleUpdateClick}
                            onDelete={onDeleteMember}
                            onShare={handleShareLink}
                        />
                    ))}

                    {paginatedMembers.length > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />
                    )}

                    {filteredMembers.length === 0 && (
                        <div className="py-20 text-center">
                            <div className="w-20 h-20 bg-white border border-slate-200 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-sm">
                                <Search size={32} className="text-slate-300" />
                            </div>
                            <h3 className="text-lg font-black text-slate-800 mb-2">검색 결과가 없습니다</h3>
                            <p className="text-sm text-slate-500 font-medium">다른 검색어나 필터를 시도해보세요.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Registration/Edit Form Modal */}
            {isFormOpen && editingMember && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200 my-8">
                        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                            <div className="flex items-center gap-2">
                                <UserPlus size={20} className="text-indigo-600" />
                                <h3 className="font-bold text-lg text-slate-800">회원 정보 수정</h3>
                            </div>
                        </div>

                        <form onSubmit={handleSaveMember} className="p-6 space-y-6 max-h-[80vh] overflow-y-auto custom-scrollbar">
                            <div className="space-y-4">
                                <label className="text-xs font-black text-slate-400 uppercase tracking-widest">기본 정보</label>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-1">이름</label>
                                        <input
                                            type="text"
                                            value={editingMember.name}
                                            onChange={(e) => setEditingMember({ ...editingMember, name: e.target.value })}
                                            className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-1">전화번호</label>
                                        <input
                                            type="tel"
                                            value={editingMember.phone || ''}
                                            onChange={(e) => setEditingMember({ ...editingMember, phone: e.target.value })}
                                            className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                                            placeholder="010-0000-0000"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-1">학교</label>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={editingMember.schoolName || ''}
                                                readOnly
                                                onClick={() => setIsSchoolModalOpen(true)}
                                                className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all cursor-pointer bg-slate-50"
                                                placeholder="학교 검색"
                                            />
                                            <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-1">학년</label>
                                        <select
                                            value={editingMember.grade || ''}
                                            onChange={(e) => setEditingMember({ ...editingMember, grade: e.target.value })}
                                            className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                                        >
                                            <option value="">선택</option>
                                            {(siteSettings.grades && siteSettings.grades.length > 0 ? siteSettings.grades : GRADE_TYPES).map(g => (
                                                <option key={g} value={g}>{g}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>


                            </div>

                            {/* Guardians Section */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest">학부모 정보</label>
                                    <button
                                        type="button"
                                        onClick={handleAddGuardian}
                                        className="text-[10px] font-bold bg-slate-50 text-indigo-600 border border-slate-200 px-2 py-1 rounded-lg hover:bg-white transition-colors flex items-center gap-1"
                                    >
                                        <Plus size={10} /> 추가
                                    </button>
                                </div>
                                <div className="space-y-3">
                                    {editingMember.guardians?.map((g, idx) => (
                                        <div key={idx} className="bg-slate-50 p-3 rounded-xl border border-slate-100 relative group">
                                            {editingMember.guardians!.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveGuardian(idx)}
                                                    className="absolute top-2 right-2 text-slate-300 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            )}
                                            <div className="grid grid-cols-4 gap-2 mb-2">
                                                <div className="col-span-1">
                                                    <label className="text-[10px] font-bold text-slate-400 block mb-1">관계</label>
                                                    <select
                                                        value={g.relation}
                                                        onChange={(e) => handleGuardianChange(idx, 'relation', e.target.value as any)}
                                                        className="w-full h-8 text-xs font-bold border-slate-200 rounded-lg px-1 bg-white"
                                                    >
                                                        <option value="부">부</option>
                                                        <option value="모">모</option>
                                                        <option value="기타">기타</option>
                                                    </select>
                                                </div>
                                                <div className="col-span-3">
                                                    <label className="text-[10px] font-bold text-slate-400 block mb-1">이름</label>
                                                    <input
                                                        type="text"
                                                        value={g.name}
                                                        onChange={(e) => handleGuardianChange(idx, 'name', e.target.value)}
                                                        className="w-full h-8 text-xs font-bold border-slate-200 rounded-lg px-2 border outline-none focus:border-indigo-500"
                                                        placeholder="보호자 성함"
                                                    />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <label className="text-[10px] font-bold text-slate-400 block mb-1">연락처</label>
                                                    <input
                                                        type="tel"
                                                        value={g.phone}
                                                        onChange={(e) => {
                                                            const val = e.target.value.replace(/[^0-9]/g, '');
                                                            if (val.length <= 11) handleGuardianChange(idx, 'phone', val);
                                                        }}
                                                        className="w-full h-8 text-xs font-bold border-slate-200 rounded-lg px-2 border outline-none focus:border-indigo-500"
                                                        placeholder="숫자만 입력"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] font-bold text-slate-400 block mb-1">이메일</label>
                                                    <input
                                                        type="email"
                                                        value={g.email}
                                                        onChange={(e) => handleGuardianChange(idx, 'email', e.target.value)}
                                                        className="w-full h-8 text-xs font-bold border-slate-200 rounded-lg px-2 border outline-none focus:border-indigo-500"
                                                        placeholder="email@example.com"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">재원 상태</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {['재원', '휴원', '퇴원'].map((status) => (
                                        <button
                                            key={status}
                                            type="button"
                                            onClick={() => setEditingMember({ ...editingMember, enrollmentStatus: status as any })}
                                            className={`py-3 rounded-xl text-sm font-bold transition-all border ${editingMember.enrollmentStatus === status
                                                ? status === '재원' ? 'bg-green-50 border-green-200 text-green-600 ring-2 ring-green-500/20' :
                                                    status === '휴원' ? 'bg-orange-50 border-orange-200 text-orange-600 ring-2 ring-orange-500/20' :
                                                        'bg-slate-50 border-slate-200 text-slate-600 ring-2 ring-slate-500/20'
                                                : 'bg-white border-slate-200 text-slate-400 hover:bg-slate-50 hover:text-slate-600'
                                                }`}
                                        >
                                            {status}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="pt-4 flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setIsFormOpen(false)}
                                    className="flex-1 px-4 py-3 rounded-xl font-bold text-slate-500 hover:bg-slate-100 transition-colors"
                                >
                                    취소
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-3 rounded-xl font-bold text-white bg-indigo-600 hover:bg-indigo-700 shadow-md shadow-indigo-200 transition-all"
                                >
                                    저장하기
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {
                isSchoolModalOpen && editingMember && (
                    <NeisSearchModal
                        onClose={() => setIsSchoolModalOpen(false)}
                        onSelect={(school) => {
                            setEditingMember({
                                ...editingMember,
                                schoolName: school.SCHUL_NM,
                                neisSchoolCode: school.SD_SCHUL_CODE,
                                neisSchoolType: school.SCHUL_KND_SC_NM,
                                neisOfficeCode: school.ATPT_OFCDC_SC_CODE,
                                neisOfficeName: school.ATPT_OFCDC_SC_NM
                            });
                            setIsSchoolModalOpen(false);
                        }}
                    />
                )
            }
        </div>
    );
};

export default MemberRoot;
